import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BillLine, Product } from '@sense/shared';
import type { CustomerRef } from './customers';

export interface CartLine { pid: number; name: string; unit: Product['unit']; qty: number; price: number; disc: number; tax_bp: number; category?: string; batch?: string; expiry?: number }

interface CartState {
  lines: CartLine[];
  selected: number;          // index into lines
  billDiscount: number;      // paise
  customer: CustomerRef | null;
  approvals: Array<[string, string]>; // [action, approver] for overrides a manager allowed
  add(p: Product, qty?: number, lot?: { batch?: string; expiry?: number }): void;
  setQty(i: number, qty: number): void;
  step(i: number, dir: 1 | -1): void;
  setDisc(i: number, disc: number): void;
  setPrice(i: number, price: number): void;
  approve(action: string, by: string): void;
  remove(i: number): void;
  select(i: number): void;
  setBillDiscount(d: number): void;
  setCustomer(c: CustomerRef | null): void;
  load(lines: CartLine[], customer: CustomerRef | null, billDiscount: number): void;
  clear(): void;
}

const defaultQty = (p: Pick<Product, 'unit'>) => (p.unit === 'g' || p.unit === 'ml' ? 100_000 : 1000);
const stepFor = (unit: Product['unit']) => (unit === 'kg' || unit === 'l' ? 250 : unit === 'g' || unit === 'ml' ? 50_000 : 1000);

export const useCart = create<CartState>()(persist((set) => ({
  lines: [],
  selected: -1,
  billDiscount: 0,
  customer: null,
  approvals: [],
  add(p, qty, lot) {
    set((s) => {
      const q = qty ?? defaultQty(p);
      // Piece items merge into one line; weighed items with an explicit weight stay separate lines.
      const merge = qty == null || p.unit === 'pc' || p.unit === 'pack';
      const i = merge ? s.lines.findIndex((l) => l.pid === p.id && l.price === p.price && l.disc === 0 && l.batch === lot?.batch && l.expiry === lot?.expiry) : -1;
      if (i >= 0) {
        const lines = s.lines.slice();
        lines[i] = { ...lines[i], qty: lines[i].qty + q };
        return { lines, selected: i };
      }
      const line: CartLine = { pid: p.id, name: p.name, unit: p.unit, qty: q, price: p.price, disc: 0, tax_bp: p.tax_bp, category: p.category, ...(lot?.batch ? { batch: lot.batch } : {}), ...(lot?.expiry ? { expiry: lot.expiry } : {}) };
      return { lines: [...s.lines, line], selected: s.lines.length };
    });
  },
  setQty: (i, qty) => set((s) => {
    if (qty <= 0) return { lines: s.lines.filter((_, j) => j !== i), selected: Math.min(s.selected, s.lines.length - 2) };
    const lines = s.lines.slice();
    lines[i] = { ...lines[i], qty };
    return { lines };
  }),
  step: (i, dir) => set((s) => {
    const l = s.lines[i];
    if (!l) return {};
    const qty = l.qty + dir * stepFor(l.unit);
    if (qty <= 0) return { lines: s.lines.filter((_, j) => j !== i), selected: Math.min(i, s.lines.length - 2) };
    const lines = s.lines.slice();
    lines[i] = { ...l, qty };
    return { lines, selected: i };
  }),
  setDisc: (i, disc) => set((s) => {
    const lines = s.lines.slice();
    lines[i] = { ...lines[i], disc: Math.max(0, disc) };
    return { lines };
  }),
  setPrice: (i, price) => set((s) => {
    const lines = s.lines.slice();
    lines[i] = { ...lines[i], price: Math.max(0, price) };
    return { lines };
  }),
  approve: (action, by) => set((s) => ({ approvals: [...(s.approvals ?? []), [action, by]] })),
  remove: (i) => set((s) => ({ lines: s.lines.filter((_, j) => j !== i), selected: Math.min(i, s.lines.length - 2) })),
  select: (i) => set((s) => ({ selected: Math.max(-1, Math.min(i, s.lines.length - 1)) })),
  setBillDiscount: (d) => set({ billDiscount: Math.max(0, d) }),
  setCustomer: (customer) => set({ customer }),
  load: (lines, customer, billDiscount) => set({ lines, customer, billDiscount, selected: lines.length - 1 }),
  clear: () => set({ lines: [], selected: -1, billDiscount: 0, customer: null, approvals: [] }),
}), {
  name: 'sd.cart',
  version: 2,
  // v1 kept the customer as a bare phone number.
  migrate: (state) => ({ ...(state as CartState), customer: typeof (state as { customer?: unknown }).customer === 'object' ? (state as CartState).customer : null }),
}));

export function packLines(lines: CartLine[]): BillLine[] {
  return lines.map((l) => [l.pid, l.qty, l.price, l.disc, l.tax_bp]);
}

